import { useState, useRef, useCallback } from 'react';
import {
  Box,
  Flex,
  Button,
  Input,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogCloseTrigger,
  DialogBackdrop,
  DialogPositioner,
  Portal,
  Spinner,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import { scrapeMenu, getTaskStatus } from '../api/menus';

interface ScrapeMenuModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (menuId: string) => void;
}

const ScrapeMenuModal = ({ open, onClose, onSuccess }: ScrapeMenuModalProps) => {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const handleClose = () => {
    if (isLoading) return;
    cleanup();
    setUrl('');
    setStatusMessage('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      toast.error('Please enter a URL');
      return;
    }

    setIsLoading(true);
    setStatusMessage('Starting menu scrape...');

    try {
      const { task_id } = await scrapeMenu({ url: trimmedUrl });
      setStatusMessage('Scraping menu... This may take 1-3 minutes.');

      pollRef.current = setInterval(async () => {
        try {
          const task = await getTaskStatus(task_id);

          if (task.status === 'SUCCESS') {
            cleanup();
            setIsLoading(false);

            if (task.result?.status === 'completed' && task.result.menu_id) {
              toast.success('Menu scraped successfully!');
              setUrl('');
              setStatusMessage('');
              onSuccess(task.result.menu_id);
              onClose();
            } else {
              const errorMsg = task.result?.error || 'Scrape completed but no menu was created';
              toast.error(errorMsg);
              setStatusMessage('');
            }
          } else if (task.status === 'FAILURE') {
            cleanup();
            setIsLoading(false);
            const errorMsg = task.result?.error || 'Scrape failed';
            toast.error(errorMsg);
            setStatusMessage('');
          }
        } catch {
          cleanup();
          setIsLoading(false);
          toast.error('Failed to check scrape status');
          setStatusMessage('');
        }
      }, 3000);
    } catch (error: any) {
      setIsLoading(false);
      const errorMessage = error.response?.data?.detail || 'Failed to start scrape';
      toast.error(errorMessage);
      setStatusMessage('');
    }
  };

  return (
    <DialogRoot open={open} onOpenChange={(e) => !e.open && handleClose()} placement="center">
      <Portal>
        <DialogBackdrop bg="blackAlpha.600" />
        <DialogPositioner>
          <DialogContent
            backgroundColor="neutral.800"
            borderWidth="1px"
            borderColor="neutral.700"
            borderRadius="8px"
            maxW="440px"
            p={{ base: '20px', md: '24px' }}
          >
            <DialogHeader pb="0">
              <DialogTitle
                fontSize="26px"
                fontWeight="500"
                color="neutral.100"
              >
                Scrape Menu from URL
              </DialogTitle>
              {!isLoading && <DialogCloseTrigger color="neutral.400" />}
            </DialogHeader>

            <form onSubmit={handleSubmit}>
              <DialogBody py="24px">
                <Flex direction="column" rowGap="16px">
                  <Flex direction="column" rowGap="8px">
                    <Box
                      fontWeight="400"
                      fontSize="14px"
                      lineHeight="150%"
                      color="neutral.300"
                    >
                      Restaurant Menu URL
                    </Box>
                    <Input
                      placeholder="https://restaurant.com/menu"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      disabled={isLoading}
                      p="10px"
                      fontWeight="400"
                      fontSize="14px"
                      lineHeight="150%"
                      color="neutral.300"
                      borderWidth="1px"
                      borderColor="neutral.700"
                      backgroundColor="neutral.800"
                      borderRadius="8px"
                      autoFocus
                      _focus={{
                        borderColor: 'neutral.600',
                        outline: 'none',
                      }}
                    />
                  </Flex>

                  {statusMessage && (
                    <Flex alignItems="center" gap="8px">
                      <Spinner size="sm" color="neutral.400" />
                      <Box
                        fontSize="13px"
                        fontWeight="400"
                        color="neutral.400"
                      >
                        {statusMessage}
                      </Box>
                    </Flex>
                  )}
                </Flex>
              </DialogBody>

              <DialogFooter gap="12px">
                <Button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  height="fit-content"
                  py="8px"
                  px="16px"
                  fontWeight="500"
                  fontSize="14px"
                  color="neutral.300"
                  backgroundColor="transparent"
                  borderWidth="1px"
                  borderColor="neutral.600"
                  _hover={{
                    borderColor: 'neutral.500',
                    backgroundColor: 'neutral.700',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={isLoading}
                  disabled={isLoading || !url.trim()}
                  height="fit-content"
                  py="8px"
                  px="16px"
                  fontWeight="500"
                  fontSize="14px"
                  color="neutral.900"
                  backgroundColor="neutral.100"
                  _hover={{
                    backgroundColor: 'neutral.200',
                  }}
                >
                  Scrape Menu
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </DialogPositioner>
      </Portal>
    </DialogRoot>
  );
};

export default ScrapeMenuModal;
